<template>
  <div>
    <PosthogEventsTable
      :title="tableTitle"
      :headers="headers"
      :items="items"
      :loading="loading"
      :options.sync="options"
      :total="total"
    >
      <template #top>
        <v-container fluid class="py-0">
          <ERow>
            <ECol cols="6" class="pl-0 pb-0 text-h6">
              {{ tableTitle }}
            </ECol>
            <v-spacer />
          </ERow>
          <ERow>
            <ECol :cols="5" class="px-0 pb-0">
              <v-select
                v-model="eventsType"
                class="pr-4"
                :items="eventsTypeOptions"
                dense
                outlined
                hide-details
                label="Events Type"
                item-value="value"
                item-text="text"
                @change="fetchPosthogEvents"
              />
            </ECol>
            <ECol
              v-if="companyId && !projectExid"
              cols="auto"
              md="3"
              sm="12"
              class="pl-0"
            >
              <CompanyProjectSelect
                :solo="false"
                :company-id="companyId"
                @change="onChangeSelectedProject"
              />
            </ECol>
            <ECol v-if="projectExid" cols="auto" md="3" sm="12" class="pl-0">
              <ProjectCameraSelect
                :solo="false"
                :project-id="projectExid"
                @change="onChangeSelectedCamera"
              />
            </ECol>
          </ERow>
          <ERow>
            <ECol cols="2" class="px-0 pb-0">
              <v-menu v-model="startDatePicker" :close-on-content-click="false">
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedStartDate"
                    clearable
                    outlined
                    dense
                    label="From"
                    placeholder="From Date"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="startDate = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  @change="fetchPosthogEvents"
                ></v-date-picker>
              </v-menu>
            </ECol>
            <ECol cols="2" class="pb-0">
              <v-menu v-model="endDatePicker" :close-on-content-click="false">
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedEndDate"
                    clearable
                    outlined
                    dense
                    label="To"
                    placeholder="To Date"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="endDate = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  @change="fetchPosthogEvents"
                ></v-date-picker>
              </v-menu>
            </ECol>
          </ERow>
        </v-container>
      </template>
      <template #item.name="{ item }">
        {{ formatEventName(item.name) }}
      </template>
      <template #item.page="{ item }">
        {{ item.page.startsWith("$") ? item.page.substring(1) : item.page }}
      </template>
      <template #item.timestamp="{ item }">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ relativeDate(item.timestamp) }}
            </span>
          </template>
          <span>
            {{ formatDate(item.timestamp) }}
          </span>
        </v-tooltip>
      </template>
    </PosthogEventsTable>
  </div>
</template>

<script>
import { debounce } from "@evercam/ui"
import { EvercamLabsApi } from "@evercam/shared/api/evercamLabsApi"
import { PosthogEventsTableHeaders } from "@evercam/shared/constants/posthog"

import CompanyProjectSelect from "@evercam/shared/components/CompanyProjectSelect"
import PosthogEventsTable from "@evercam/shared/components/posthog/PosthogEventsTable"
import ProjectCameraSelect from "@evercam/shared/components/ProjectCameraSelect"

export default {
  components: {
    CompanyProjectSelect,
    PosthogEventsTable,
    ProjectCameraSelect,
  },
  props: {
    companyExid: {
      type: String,
      default: "",
    },
    companyId: {
      type: [Number, null],
      default: null,
    },
    projectExid: {
      type: String,
      default: "",
    },
    email: {
      type: String,
      default: "",
    },
    isDisplayedInternalData: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      endDate: this.$moment().format("YYYY-MM-DD"),
      endDatePicker: false,
      eventsType: "product",
      eventsTypeOptions: [
        { text: "Product", value: "product" },
        { text: "All", value: "" },
      ],
      headers: PosthogEventsTableHeaders(this),
      baseHeaders: PosthogEventsTableHeaders(this),
      items: [],
      loading: true,
      options: {
        sortBy: ["timestamp"],
        sortDesc: [true],
      },
      selectedPeriod: "monthly",
      selectedCameraExid: null,
      selectedProjectExid: null,
      startDate: this.$moment().subtract(30, "d").format("YYYY-MM-DD"),
      startDatePicker: false,
      total: 0,
    }
  },
  computed: {
    formattedEndDate() {
      return this.endDate ? this.formatDate(this.endDate, "DD MMM YYYY") : ""
    },
    formattedStartDate() {
      return this.startDate
        ? this.formatDate(this.startDate, "DD MMM YYYY")
        : ""
    },
    periodFilterColClass() {
      return { "px-0": this.$vuetify.breakpoint.smAndDown }
    },
    tableTitle() {
      return "Events"
    },
  },
  watch: {
    options(value, oldValue) {
      let isRefreshingTable = false

      // Check if 'paging' has changed
      if (
        value.page !== oldValue.page ||
        value.itemsPerPage !== oldValue.itemsPerPage
      ) {
        isRefreshingTable = true
      }
      // Check if 'sort' has changed && is a valid sort option
      const sortOptions = [
        "timestamp",
        "name",
        "page",
        "context",
        "email",
        "companyExid",
        "projectExid",
        "cameraExid",
      ]
      if (
        (value?.sortBy !== oldValue?.sortBy ||
          value?.sortDesc !== oldValue?.sortDesc) &&
        sortOptions.includes(value?.sortBy[0])
      ) {
        isRefreshingTable = true
      }
      if (!isRefreshingTable) {
        return
      }
      this.fetchPosthogEvents()
    },
  },
  async mounted() {
    await this.fetchPosthogEvents()
  },
  methods: {
    fetchPosthogEvents: debounce(async function () {
      if (
        !this.email &&
        !this.companyExid &&
        !this.projectExid &&
        !this.selectedProjectExid
      ) {
        return
      }

      const sortOptionsMapping = {
        timestamp: "timestamp",
        name: "name",
        page: "eventPageId",
        context: "eventContext",
        email: "email",
        companyExid: "companyExid",
        projectExid: "projectExid",
        cameraExid: "cameraExid",
      }
      const sortBy = sortOptionsMapping[this.options.sortBy[0]]
      const sortDesc = this.options.sortDesc[0]
      const fullEndDate = this.$moment(this.endDate)
        .add(1, "d")
        .subtract(1, "s")
        .toISOString()

      const params = {
        companyExid: this.companyExid,
        projectExid: this.projectExid ?? this.selectedProjectExid,
        cameraExid: this.selectedCameraExid,
        email: this.email,
        before: this.endDate ? fullEndDate : "",
        after: this.startDate ? this.$moment(this.startDate).toISOString() : "",
        page: this.options.page,
        size: this.options.itemsPerPage,
        type: this.eventsType, // product: Gets only product-listed events (by default: gets all events)
        sort: sortBy ? `${sortBy}${sortDesc ? ",desc" : ""}` : null,
      }

      try {
        this.loading = true

        const response = await EvercamLabsApi.posthog.getEvents(params)
        this.total = response?.total ?? 0

        response?.data.forEach((element, key) => {
          // Format item attributes to be usable in the data-table
          element.index = key
          element.projectExid = element.properties?.projectExid
          element.projectName = element.properties?.projectName
          element.cameraExid = element.properties?.cameraExid
          element.cameraName = element.properties?.cameraName
          element.browser = `${element.properties?.$browser} ${element.properties?.$browserVersion} on ${element.properties?.$os}`
          element.ipAddress = element.properties?.$ip
        })

        this.items = response.data
      } catch (error) {
        this.$notifications.error({
          text: "Could not load Posthog Events!",
          error,
        })
      } finally {
        this.loading = false
      }
    }, 1000),
    formatDate(date, format = "DD MMM YYYY HH:mm:ss") {
      return this.$moment(date).isValid()
        ? this.$moment(date).format(format)
        : "-"
    },
    formatEventName(name) {
      const nameParts = name.split("-").slice(1)

      return name.startsWith("$") ? name.substring(1) : nameParts.join(" > ")
    },
    onChangeSelectedCamera(selectedCameraExid) {
      this.selectedCameraExid = selectedCameraExid
      this.fetchPosthogEvents()
    },
    onChangeSelectedProject(selectedProjectExid) {
      this.selectedProjectExid = selectedProjectExid
      this.fetchPosthogEvents()
    },
    relativeDate(date) {
      return this.$moment(date).isValid() ? this.$moment(date).fromNow() : "-"
    },
  },
}
</script>
